import * as React from "react";
import {
  Autocomplete,
  TextField,
  Checkbox,
  Chip,
  ListItemText,
  ThemeProvider,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { setResultVisualizationExpFilter } from "../../store/filterSlice";
import { filterOptions, theme } from "../../components/Filters/FiltersCommon";

type InputTypeProps = {
  itemList?: string[];
  itemType: string;
  itemLabel?: string;
  onFilterChange?: (values: string[]) => void;
};

export default function MultipleSelectVisualizationExp(
  itemsProps: Readonly<InputTypeProps>
) {
  const dispatch = useDispatch();
  const filter = useSelector(
    (state: RootState) => state.resultVisualizationExpFilter
  );
  const userData = useSelector((state: RootState) => state.user);

  const [selectAll, setSelectAll] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [pendingAdditions, setPendingAdditions] = useState<{
    key: keyof typeof filter;
    value: string[];
    label?: string[];
  } | null>(null);
  const previousValuesRef = useRef<string[]>([]);

  const defaultDisabledLabels =
    userData?.currentSector?.sector === "PFNA" &&
    userData?.filterData?.PFNA?.Customer?.defaultDisabledLabels?.[0];

  const disabledLabel =
    userData?.currentSector?.sector === "PFNA" &&
    userData?.filterData?.PFNA?.PFE?.disabledLabels?.[filter?.ac1ac2?.[0]]?.[0]
      ?.Item?.[0];

  const isDisabled =
    userData?.currentSector?.sector === "PFNA" &&
    (disabledLabel === itemsProps?.itemLabel ||
      defaultDisabledLabels === itemsProps?.itemLabel);

  const itemKey = itemsProps.itemType as keyof typeof filter;
  const isSpecialSplitField = [
    "keyAccount",
    "salesRegion",
    "planningItem",
    "zone",
  ].includes(itemsProps.itemType);

  const allOptions = useMemo(() => {
    const base = itemsProps.itemList || [];
    return base.length > 2 ? ["Select All", ...base] : base;
  }, [itemsProps.itemList]);

  useEffect(() => {
    if (defaultDisabledLabels) {
      dispatch(
        setResultVisualizationExpFilter({ key: "ibpDemandGroup", value: [] })
      );
    }
    if (isDisabled) {
      if (itemsProps.itemLabel === "Planning Item") {
        dispatch(
          setResultVisualizationExpFilter({ key: "planningItem", value: [] })
        );
      } else if (itemsProps.itemLabel === "Each UPC") {
        dispatch(
          setResultVisualizationExpFilter({ key: "eachUPC", value: [] })
        );
      }
    }
  }, [defaultDisabledLabels, isDisabled, itemsProps.itemLabel, dispatch]);

  useEffect(() => {
    const currentValues =
      itemsProps.itemType === "keyAccount"
        ? filter?.keyAccountLabel || []
        : filter[itemKey] || [];

    setSelectedValues(currentValues);
    previousValuesRef.current = currentValues;
    setSelectAll(itemsProps.itemList?.length === currentValues.length);
  }, [filter, itemKey, itemsProps.itemList, itemsProps.itemType]);

  const dispatchChanges = (key: keyof typeof filter, value: string[]) => {
    dispatch(setResultVisualizationExpFilter({ key, value }));
  };

  const handleChange = (_: React.SyntheticEvent | null, value: string[]) => {
    let processedValue: string[] = [];

    if (value.includes("Select All")) {
      const filteredList = filterOptions(allOptions, {
        inputValue,
        getOptionLabel: (option) => option,
      }).filter((v) => v !== "Select All");
      processedValue = selectAll ? [] : filteredList;
      setSelectAll(!selectAll);
    } else {
      processedValue = value;
      setSelectAll(itemsProps.itemList?.length === value.length);
    }

    if (processedValue.length === 0 && pendingAdditions) {
      if (itemsProps.itemType === "keyAccount" && pendingAdditions.label) {
        dispatchChanges("keyAccountLabel", []);
      }
      dispatchChanges(itemKey, []);
      setPendingAdditions(null);
    }

    const splitValues = processedValue.map((val) => {
      const parts = val.split(" - ");
      if (parts.length < 2) {
        console.warn(`Unexpected format: ${val}`);
      }
      const index = itemsProps.itemType === "keyAccount" ? 1 : 0;
      return parts[index]?.trim() || val.trim();
    });

    const isRemoving = processedValue.length < previousValuesRef.current.length;
    previousValuesRef.current = processedValue;
    setSelectedValues(processedValue);

    if (isRemoving) {
      if (itemsProps.itemType === "keyAccount") {
        dispatchChanges("keyAccountLabel", processedValue);
        dispatchChanges(itemKey, splitValues); // ✅ use split values here
      } else if (isSpecialSplitField) {
        dispatchChanges(itemKey, splitValues);
      } else {
        dispatchChanges(itemKey, processedValue);
      }
    } else {
      const additions = {
        key: itemKey,
        value: isSpecialSplitField ? splitValues : processedValue,
        label:
          itemsProps.itemType === "keyAccount" ? processedValue : undefined,
      };
      setPendingAdditions(additions);
    }
  };

  const handleClose = () => {
    if (!pendingAdditions) return;
    const { key, value, label } = pendingAdditions;
    if (itemsProps.itemType === "keyAccount" && label) {
      dispatchChanges("keyAccountLabel", label);
    }
    dispatchChanges(key, value);
    setPendingAdditions(null);
  };



  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const paste = event.clipboardData.getData("text");
    const tokens = paste
      .split(/[,;\n\r]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

    if (!tokens.length) return;
    const matchedSet = new Set<string>();

    tokens.forEach((token) => {
      const lowerToken = token.toLowerCase();
      (itemsProps.itemList || []).forEach((option) => {
        if (option.toLowerCase().includes(lowerToken)) {
          matchedSet.add(option);
        }
      });
    });

    const matched = Array.from(matchedSet);

    if (tokens.includes("select all") && itemsProps.itemList?.length) {
      matched.splice(0, matched.length, ...itemsProps.itemList);
    }

    handleChange(null, matched);
    setInputValue("");
  };

  return (
    <div className="flex">
      <ThemeProvider theme={theme}>
        <Autocomplete
          disabled={isDisabled}
          multiple
          filterOptions={filterOptions}
          options={allOptions}
          value={isDisabled ? [] : selectedValues}
          onChange={handleChange}
          onClose={() => {
            handleClose();
            setIsOpen(false);
            setInputValue("");
          }}
          onOpen={() => setIsOpen(true)}
          open={isOpen}
          inputValue={isOpen ? inputValue : ""}
          onInputChange={(event, value, reason) => {
            if (reason === 'input') {
              setInputValue(value);
            }
          }}
          disableCloseOnSelect
          noOptionsText="No options"
          blurOnSelect={false}
          forcePopupIcon={false}
          disablePortal
          ListboxProps={{ style: { maxHeight: 300 } }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select"
              autoComplete="off"
              inputProps={{ ...params.inputProps, onPaste: handlePaste }}
              sx={{
                "& .MuiInputBase-root": { color: "white" },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#616161",
                },
                "& .MuiSvgIcon-root": { color: "white" },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    border: "none",
                  },
              }}
            />
          )}
          renderTags={(selected, getTagProps) => {
            const visible = selected
              .filter((tag) => tag !== "Select All")
              .slice(0, 5);
            const hidden =
              selected.filter((tag) => tag !== "Select All").length - 5;

            return [
              ...visible.map((option, index) => (
                <Chip label={option} {...getTagProps({ index })} />
              )),
              hidden > 0 && (
                <Chip
                  key="more"
                  label={`+${hidden}`}
                  sx={{
                    backgroundColor: "#2196F3",
                    color: "white",
                    borderRadius: "8px",
                    padding: "4px",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                />
              ),
            ];
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option}>
              <Checkbox
                checked={option === "Select All" ? selectAll : selected}
                sx={{ color: "white", "&.Mui-checked": { color: "white" } }}
              />
              <ListItemText
                primary={option}
                sx={{
                  "& .MuiTypography-root": {
                    fontWeight: option === "Select All" ? 600 : 400,
                  },
                }}
              />
            </li>
          )}
          sx={{
            width: "90%",
            "& .MuiAutocomplete-tag": { color: "white" },
          }}
        />
      </ThemeProvider>
    </div>
  );
}
